import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/contact-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      // Use defaults if fetch fails
      setSettings({
        page_title: 'Contact Us',
        page_subtitle: 'Get in touch with ICAR-Central Institute for Subtropical Horticulture',
        address: 'ICAR-Central Institute for Subtropical Horticulture\nRehmankhera, P.O. Kakori\nLucknow - 226101\nUttar Pradesh, India',
        phone: '+91-522-2841022, 2841023',
        fax: '+91-522-2841025',
        email: 'director.cish@icar.gov.in',
        alternate_email: 'cishlko@gmail.com',
        office_hours: 'Monday - Friday: 9:00 AM - 5:30 PM\nSaturday: 9:00 AM - 1:00 PM',
        google_maps_embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3558.2!2d80.85!3d26.92!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDU1JzEyLjAiTiA4MMKwNTEnMDAuMCJF!5e0!3m2!1sen!2sin!4v1!5m2!1sen!2sin',
        form_title: 'Send us a Message',
        form_recipient_email: 'director.cish@icar.gov.in'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Construct mailto URL with form data
    const recipient = settings?.form_recipient_email || 'director.cish@icar.gov.in';
    const subject = encodeURIComponent(formData.subject);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone || 'Not provided'}\n\n` +
      `Message:\n${formData.message}`
    );
    
    const mailtoUrl = `mailto:${recipient}?subject=${subject}&body=${body}`;
    
    // Open default email client
    window.location.href = mailtoUrl;
    
    // Reset form after a brief delay
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0E6D2B] border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-[#6B6B6B]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main id="main-content" className="py-8 md:py-12">
        <div className="container-cish">
          {/* Page Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E6D2B] mb-3">
              {settings?.page_title || 'Contact Us'}
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              {settings?.page_subtitle || 'Get in touch with ICAR-Central Institute for Subtropical Horticulture'}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-[#F4F8EF] rounded-xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-[#0E6D2B] mb-6">Contact Information</h2>
                
                <div className="space-y-5">
                  {/* Address */}
                  {settings?.address && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#0E6D2B] flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">Address</h3>
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                          {settings.address}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Phone */}
                  {settings?.phone && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#0E6D2B] flex items-center justify-center shrink-0">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">Phone</h3>
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                          {settings.phone}
                          {settings.fax && (
                            <>
                              <br />
                              Fax: {settings.fax}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {settings?.email && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#0E6D2B] flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">Email</h3>
                        <p className="text-gray-600 text-sm mt-1">
                          <a href={`mailto:${settings.email}`} className="hover:text-[#0E6D2B] hover:underline">
                            {settings.email}
                          </a>
                          {settings.alternate_email && (
                            <>
                              <br />
                              <a href={`mailto:${settings.alternate_email}`} className="hover:text-[#0E6D2B] hover:underline">
                                {settings.alternate_email}
                              </a>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Office Hours */}
                  {settings?.office_hours && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#0E6D2B] flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">Office Hours</h3>
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                          {settings.office_hours}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Google Maps */}
              {settings?.google_maps_embed_url && (
                <div className="rounded-xl overflow-hidden h-64 md:h-80 bg-gray-200">
                  <iframe
                    src={settings.google_maps_embed_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="CISH Location"
                  />
                </div>
              )}
            </div>

            {/* Contact Form - mailto */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8">
              <h2 className="text-xl font-semibold text-[#0E6D2B] mb-6">
                {settings?.form_title || 'Send us a Message'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter your name" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                    <Input 
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Enter your email" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <Input 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Enter your phone number" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject *</label>
                  <Input 
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    placeholder="Enter message subject" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] focus:border-transparent resize-none"
                    placeholder="Enter your message"
                  />
                </div>

                <Button type="submit" className="w-full sm:w-auto bg-[#0E6D2B] hover:bg-[#054018]">
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
                
                <p className="text-xs text-gray-500 mt-2">
                  📧 This will open your default email application with the message pre-filled
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
